import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, CheckCircle2, Circle } from "lucide-react";
import { EventFeedItem } from "./EventFeedItem";
import type { ScanFeedEvent } from "@/hooks/useScanEvents";

type PhaseStatus = "pending" | "active" | "completed";

interface ScanPhaseSectionProps {
  value: string;
  title: string;
  status: PhaseStatus;
  current?: number;
  total?: number;
  summary?: string;
  events: ScanFeedEvent[];
  onLocationClick?: (lat: number, lng: number) => void;
}

export function ScanPhaseSection({
  value,
  title,
  status,
  current = 0,
  total = 0,
  summary,
  events,
  onLocationClick,
}: ScanPhaseSectionProps) {
  const percent = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <AccordionItem value={value} className="border-b last:border-b-0">
      <AccordionTrigger className="px-4 py-2.5 text-sm hover:no-underline">
        <div className="flex items-center gap-2 w-full">
          {status === "active" && <Loader2 className="size-3.5 animate-spin text-blue-500" />}
          {status === "completed" && <CheckCircle2 className="size-3.5 text-green-500" />}
          {status === "pending" && <Circle className="size-3.5 text-muted-foreground/40" />}
          <span className={status === "pending" ? "text-muted-foreground/60" : ""}>{title}</span>
          {status === "completed" && summary && (
            <span className="ml-auto text-xs text-muted-foreground font-normal pr-2">{summary}</span>
          )}
          {status === "active" && total > 0 && (
            <span className="ml-auto text-xs text-muted-foreground font-normal pr-2">
              {current}/{total}
            </span>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-3">
        {status === "active" && total > 0 && (
          <Progress value={percent} className="h-1.5 mb-2" />
        )}
        {events.length > 0 ? (
          <ScrollArea className="max-h-[200px]">
            <div className="space-y-0.5">
              {events.map((e) => (
                <EventFeedItem
                  key={e.id}
                  feedEvent={e}
                  onLocationClick={onLocationClick}
                />
              ))}
            </div>
          </ScrollArea>
        ) : status === "active" ? (
          <div className="text-xs text-muted-foreground py-2">Waiting for events...</div>
        ) : null}
      </AccordionContent>
    </AccordionItem>
  );
}
